import React, { useState, useMemo } from 'react';

import { Checkbox, Popover, Input, Spin, Button, notification, Segmented } from 'antd';
import Axios from 'axios';

import styled from 'styled-components';

import { isJobEvent } from '@libs/EventTypes';
import { showDateNoTime } from '@libs/NwMoment';

import { getContactName } from '@hooks/API/Contact/UseContactsList';

import VerticalDate from '@components/Dates/VerticalDate';
import OptionNumber from '@components/EventDrawer/EventDrawerUi/OptionNumber';
import FlexContainer from '@components/Gui/FlexContainer';
import MiniLoading from '@components/Gui/MiniLoading';
import { NwButton, NwButtonGroup, NwRemoveButton } from '@components/Gui/NwButton';
import NwDividerVertical from '@components/Gui/NwDividerVertical';
import RadioButtonGroup from '@components/Gui/NwForm/RadioButtonGroup';
import NwIcon from '@components/Gui/NwIcon';
import StyleVariables from '@components/Gui/StyleVariables';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const drawerWidth = '60rem';

const ErrorsWrapper = styled.div`
    h2 {
        font-size: ${(props) => props.theme.FontSizeLg};
        font-weight: 600;
        color: ${(props) => props.theme.DangerColor};
        padding: 1rem 3rem;
    }

    .removal-back {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 1rem 2rem;
        cursor: pointer;
    }

    .removal-errors {
        .removal-error {
            margin-bottom: 1rem;
            border-bottom: ${(props) => props.theme.DefaultBorder};
            padding: 1rem 3rem;

            .removal-error-title {
                margin: 0;
                font-size: ${(props) => props.theme.FontSizeMedium};
                font-weight: 600;
            }

            .removal-error-content {
                margin-top: 0.5rem;
                display: flex;
                align-items: flex-start;
                justify-content: flex-start;
                gap: 1.5rem;

                .removal-error-content-icon {
                    font-size: 1.5rem;
                }
                .removal-error-content-text {
                    p {
                        &.locked-transactions-text {
                            color: ${(props) => props.theme.DangerColor};
                            font-weight: 600;
                        }
                    }
                }
            }
        }
    }
`;

const Header = styled.div`
    border-bottom: 1px solid #e8e8e8;
    background-color: ${(props) => props.theme.LayoutBodyBackground};
    position: sticky;
    top: 0;
    background: white;
    z-index: 1;
    width: 100%;
    height: 4rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 2rem;

    @media (max-width: 768px) {
        flex-direction: column;
        position: relative;
        height: auto;
        gap: 1rem;
        padding: 1rem;
    }

    p.n-selected {
        margin: 0;
    }
    div.select-actions {
        min-width: 11rem;
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        justify-content: space-between;
    }
`;

const Footer = styled.div`
    border-top: 1px solid #e8e8e8;
    position: fixed;
    bottom: -6rem;
    transition: bottom 0.3s ease-in-out;
    height: 6rem;
    background: white;
    z-index: 1;
    width: ${drawerWidth};

    @media (max-width: 768px) {
        width: 100%;
        bottom: -15rem;
        height: 15rem;
        padding: 1rem;
    }

    &.is-visible {
        bottom: 0;
    }

    .footer-wrapper {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 1rem 2rem;
        width: 100%;

        @media (max-width: 768px) {
            flex-direction: column;
            align-items: center;
            gap: 1rem;
            padding: 1rem;
        }

        label.footer-label {
            text-transform: uppercase;
            font-size: ${(props) => props.theme.FontSizeXSm};
            font-weight: 600;
            color: ${(props) => props.theme.TextColorMuted};
            display: block;
            margin-bottom: 0.5rem;
        }

        .footer-left {
            .footer-left-buttons {
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 1rem;
                flex-wrap: nowrap;
            }
        }
        .footer-middle {
            text-align: center;
            &.full-width {
                flex: 1 1 0;
            }
        }
        .footer-right {
            text-align: right;

            @media (max-width: 768px) {
                text-align: center;
            }
        }
    }
`;

const DatesContainer = styled.div`
    padding-bottom: 6rem;

    .date {
        display: flex;
        align-items: center;
        width: 100%;
        border-bottom: ${(props) => props.theme.DefaultBorder};

        .date-selection-checkbox-wrapper {
            padding: 0 1rem 0 2rem;
        }

        .models-list {
            flex: 1 1 0;
            padding: 1rem 2rem;
            border-left: ${(props) => props.theme.DefaultBorder};
            .model {
                margin: 0.5rem 0;
                display: flex;
                width: 100%;
                align-items: center;
                justify-content: space-between;

                .model-left {
                    display: flex;
                    align-items: center;
                    justify-content: flex-start;
                    gap: 0.5rem;
                    p {
                        margin: 0 0 0 0.5rem;
                        text-transform: capitalize;
                        svg {
                            margin-right: 0.5rem;
                        }
                    }
                }
                .model-right {
                }

                &.model-cancelled {
                    .model-left {
                        p {
                            ${(props) => props.theme.RedLineThrough(1)}
                        }
                    }
                }
            }
        }
    }
`;

const CancellationWrapper = styled.div`
    width: 240px;
    button {
        border-radius: 6px !important;
        border-right: 1px solid ${(props) => props.theme.LighterBorderColor} !important;
        font-size: 1rem !important;
    }
`;

const ButtonGroupWrapper = styled.div`
    margin-right: 16px;
    .ant-btn:nth-child(2),
    span:nth-child(2) > .ant-btn,
    a:nth-child(2) > .ant-btn {
        border-right: 1px solid #d9d9d9 !important;
    }
    .ant-btn:nth-child(2),
    span:nth-child(2) > .ant-btn,
    a:nth-child(2) > .ant-btn {
        border-top-right-radius: 6px;
        border-bottom-right-radius: 6px;
    }
`;

const SelectLink = styled.div`
    color: ${(props) => props.theme.PrimaryColor};
    text-decoration: underline;
    cursor: pointer;
    font-size: 0.95rem;
    font-weight: normal;
`;

const ManageModelsAndDates = ({ eventData, afterUpdate }) => {
    const [selectedDateIds, setSelectedDateIds] = useState([]);
    const [selectedModelIds, setSelectedModelIds] = useState([]);
    const optionNumber = 0;
    const [customerType, setCustomerType] = useState('Client');
    const [notes, setNotes] = useState('');
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);

    // check if there's at least one date with models
    const hasSomeModels = eventData.EventDates.some((eventDate) => eventDate.EventDatesModels.length);
    const [workOn, setWorkOn] = useState(hasSomeModels ? 'models' : 'dates');
    const [hasModels, setHasModels] = useState(hasSomeModels);
    const [removalErrors, setRemovalErrors] = useState([]);

    const changeWorkOn = (workOn) => {
        setWorkOn(workOn);
        setSelectedDateIds([]);
        setSelectedModelIds([]);
    };

    const eventDatesWithModelNames = useMemo(() => {
        const filteredDates =
            workOn === 'dates'
                ? eventData.EventDates
                : eventData.EventDates.filter((eventDate) => eventDate.EventDatesModels.length);
        const dataWithModelNames = filteredDates
            .sort((a, b) => {
                return a.FromDate.localeCompare(b.FromDate);
            })
            .map((eventDate) => {
                return {
                    ...eventDate,
                    EventDatesModels: eventDate.EventDatesModels.map((eventDateModel) => {
                        return {
                            ...eventDateModel,
                            ModelName: getContactName(eventDateModel.ModelID),
                        };
                    }),
                };
            });
        return dataWithModelNames.map((eventDate) => {
            return {
                ...eventDate,
                EventDatesModels: eventDate.EventDatesModels.sort((a, b) => {
                    return a.ModelName.localeCompare(b.ModelName);
                }),
            };
        });
    }, [eventData, workOn]);

    const eventDatesModels = useMemo(() => {
        const edm = eventDatesWithModelNames.flatMap((eventDate) => eventDate.EventDatesModels);
        return edm;
    }, [eventDatesWithModelNames]);

    const updateSelectedModelIds = (modelIds) => {
        //ids should be unique
        const uniqueModelIds = [...new Set(modelIds)];
        setSelectedModelIds(uniqueModelIds);
    };

    const updateSelectedDateIds = (dateIds) => {
        //ids should be unique
        const uniqueDateIds = [...new Set(dateIds)];
        setSelectedDateIds(uniqueDateIds);
    };

    const handleSelectModel = (modelId, event) => {
        if (event.target.checked) {
            updateSelectedModelIds([...selectedModelIds, modelId]);
        } else {
            updateSelectedModelIds(selectedModelIds.filter((eventModelId) => eventModelId !== modelId));
        }
    };

    const handleSelectDate = (dateId, event) => {
        if (event.target.checked) {
            updateSelectedDateIds([...selectedDateIds, dateId]);
        } else {
            updateSelectedDateIds(selectedDateIds.filter((eventDateId) => eventDateId !== dateId));
        }
    };

    const handleSelectAll = () => {
        if (workOn === 'models') {
            updateSelectedModelIds(eventDatesModels.map((eventDateModel) => eventDateModel.ID));
        } else {
            updateSelectedDateIds(eventDatesWithModelNames.map((eventDate) => eventDate.ID));
        }
    };

    const handleDeselectAll = () => {
        if (workOn === 'models') {
            setSelectedModelIds([]);
        } else {
            setSelectedDateIds([]);
        }
    };

    const handleSelectDateModels = (eventDateModels) => () => {
        const newModelIds = [...selectedModelIds, ...eventDateModels.map((model) => model.ID)];
        updateSelectedModelIds(newModelIds);
    };

    const handleDeselectDateModels = (eventDateModels) => () => {
        const filteredModelIds = selectedModelIds.filter(
            (modelId) => !eventDateModels.find((dateModel) => dateModel.ID === modelId)
        );
        updateSelectedModelIds(filteredModelIds);
    };

    const handleSelectAllModels = (modelId) => () => {
        const sameModelIds = eventDatesModels.filter((model) => model.ModelID === modelId).map((model) => model.ID);
        const newModelIds = [...selectedModelIds, ...sameModelIds];
        updateSelectedModelIds(newModelIds);
    };

    const handleDeselectAllModels = (modelId) => () => {
        const modelIds = eventDatesModels.filter((dateModel) => dateModel.ModelID === modelId).map((model) => model.ID);
        const filteredModelIds = selectedModelIds.filter((modelId) => !modelIds.includes(modelId));
        updateSelectedModelIds(filteredModelIds);
    };

    // operations on models

    const handleUpdateAcknowledgement = (value) => async () => {
        if (selectedModelIds.length > 0) {
            setLoading(true);
            try {
                await Axios.put(
                    `/eventdatesmodels/AcknowledgeMulti/${value ? 'Checked' : 'NotChecked'}`,
                    selectedModelIds
                );
                await afterUpdate();
                setSelectedModelIds([]);
                setLoading(false);
            } catch (error) {
                setLoading(false);
                console.error('Update Acknowledgement Error => ', error);
            }
        }
    };

    const handleUpdateOptionNumber = async (optionNumber) => {
        if (selectedModelIds.length > 0) {
            setLoading(true);
            try {
                await Axios.put(`/eventdatesmodels/UpdateOptionNumberMulti`, {
                    OptionNumber: optionNumber,
                    EventDatesModelsIDs: selectedModelIds,
                });
                await afterUpdate();
                setSelectedModelIds([]);
                setLoading(false);
            } catch (error) {
                setLoading(false);
                console.error('Update Acknowledgement Error => ', error);
            }
        }
    };

    const handleCancel = (cancel) => async () => {
        if (selectedModelIds.length > 0) {
            setLoading(true);
            try {
                await Axios.put('/eventdatesmodels/Cancel', {
                    Cancel: {
                        Cancel: cancel,
                        CancelledBy: cancel ? customerType : '',
                        CancellationNote: cancel ? notes : '',
                    },
                    EventDateModelsIDs: selectedModelIds,
                });
                await afterUpdate();
                setSelectedModelIds([]);
                setNotes('');
                setCustomerType('Client');
                setVisible(false);
                setLoading(false);
            } catch (error) {
                setLoading(false);
                console.error('Update Acknowledgement Error => ', error);
            }
        }
    };

    const forceRemoveModelDate = async (edmID) => {
        try {
            await Axios.delete(`/eventdatesmodels/${edmID}?force=true`);
            //update errors and event
            setRemovalErrors(removalErrors.filter((error) => error.eventDateModelId !== edmID));
            await afterUpdate();
        } catch (error) {
            notification.error({
                message: 'Remove Event Date Model Error',
                description: error.message,
            });
        }
    };

    const handleRemoveModelsFromDates = async () => {
        // Prevent multiple executions
        if (loading) return;

        setLoading(true);
        const errors = [];

        try {
            try {
                await Axios.delete(`/eventdatesmodelsmulti`, {
                    data: {
                        EventID: eventData.ID,
                        ProjectID: eventData.Project.ID,
                        EventDatesModelsIDs: selectedModelIds,
                        ForceDeleteTransactions: false,
                    },
                });
            } catch (error) {
                console.error('Remove models error:', error);
                if (error.data && error.data.length > 0) {
                    for (const singleError of error.data) {
                        errors.push({
                            errorCode: singleError.ErrorCode,
                            showForceDelete: singleError.ErrorCode === 'DateWithLockedTransactions',
                            message: singleError.Message,
                            eventDateModelId: singleError.EventDateModelID,
                            modelID: singleError.ModelID,
                            eventDate: showDateNoTime(singleError.EventDate),
                        });
                    }
                }
            }
            // Use Promise.allSettled to wait for all API calls to complete
            // const results = await Promise.allSettled(
            //     selectedModelIds.map(async (id) => {
            //         try {
            //             await Axios.delete(`/eventdatesmodels/${id}`);
            //             return { success: true, id };
            //         } catch (error) {
            //             const evdatmod = eventDatesModels.find((edm) => edm.ID === id);
            //             return {
            //                 success: false,
            //                 id,
            //                 error: {
            //                     errorCode: error.errorCode,
            //                     showForceDelete: error.errorCode === 'DateWithLockedTransactions',
            //                     eventDateModelId: id,
            //                     modelName: evdatmod.ModelName,
            //                     eventDate: showDateNoTime(evdatmod.FromDate),
            //                 },
            //             };
            //         }
            //     })
            // );
            // results.forEach((result) => {
            //     if (result.status === 'fulfilled' && !result.value.success) {
            //         errors.push(result.value.error);
            //     } else if (result.status === 'rejected') {
            //         console.error('Unexpected error:', result.reason);
            //     }
            // });

            // Set errors if any occurred
            if (errors.length > 0) {
                setRemovalErrors(errors);
            }

            // Always call afterUpdate to refresh the data
            await afterUpdate();

            // Clear selection after the operation (regardless of errors)
            setSelectedModelIds([]);
        } catch (error) {
            console.error('Remove models error:', error);
        } finally {
            setLoading(false);
        }
    };

    // operations on dates

    const forceRemoveDate = async (edID) => {
        try {
            await Axios.delete(`/eventdates/${edID}?force=true`);
            //update errors and event
            setRemovalErrors(removalErrors.filter((error) => error.eventDateId !== edID));
            await afterUpdate();
        } catch (error) {
            notification.error({
                message: 'Remove Event Date Error',
                description: error.message,
            });
        }
    };

    const handleRemoveDates = async () => {
        // Prevent multiple executions
        if (loading) return;

        setLoading(true);
        const errors = [];

        try {
            // Use Promise.allSettled to wait for all API calls to complete
            const results = await Promise.allSettled(
                selectedDateIds.map(async (id) => {
                    try {
                        await Axios.delete(`/eventdates/${id}`);
                        return { success: true, id };
                    } catch (error) {
                        const evdat = eventDatesWithModelNames.find((ed) => ed.ID === id);
                        return {
                            success: false,
                            id,
                            error: {
                                errorCode: error.errorCode,
                                showForceDelete: error.errorCode === 'DateWithLockedTransactions',
                                eventDateId: id,
                                eventDate: showDateNoTime(evdat.FromDate),
                            },
                        };
                    }
                })
            );

            // Collect errors from failed operations
            results.forEach((result) => {
                if (result.status === 'fulfilled' && !result.value.success) {
                    errors.push(result.value.error);
                } else if (result.status === 'rejected') {
                    console.error('Unexpected error:', result.reason);
                }
            });

            // Set errors if any occurred
            if (errors.length > 0) {
                setRemovalErrors(errors);
            }

            // Always call afterUpdate to refresh the data
            await afterUpdate();

            // Clear selection after the operation (regardless of errors)
            setSelectedDateIds([]);
        } catch (error) {
            console.error('Remove dates error:', error);
        } finally {
            setLoading(false);
        }
    };

    if (removalErrors.length > 0) {
        if (workOn === 'dates') {
            return (
                <ErrorsWrapper>
                    <div className="removal-back" onClick={() => setRemovalErrors([])}>
                        <NwIcon icon={byPrefixAndName.fasl['arrow-left']} />
                        <span>Back to models & dates management</span>
                    </div>
                    <h2>Some dates could not be removed</h2>
                    <div className="removal-errors">
                        {removalErrors.map((error) => (
                            <div key={error.eventDateId} className="removal-error">
                                <h4 className="removal-error-title">{error.eventDate}</h4>
                                {error.errorCode === 'DateWithLockedTransactions' && (
                                    <div className="removal-error-content">
                                        <div className="removal-error-content-icon">
                                            <NwIcon danger icon={byPrefixAndName.fasl['file-invoice']} />
                                        </div>
                                        <div className="removal-error-content-text">
                                            <p className="locked-transactions-text">
                                                This date has locked transactions (invoiced or paid) and cannot be
                                                removed.
                                            </p>
                                        </div>
                                    </div>
                                )}
                                {error.errorCode === 'DateWithTransactions' && (
                                    <div className="removal-error-content">
                                        <div className="removal-error-content-icon">
                                            <NwIcon danger icon={byPrefixAndName.fasl['message-dollar']} />
                                        </div>
                                        <div className="removal-error-content-text">
                                            <p>
                                                This date has linked transactions. To remove this date, click on the
                                                button below. All transactions for this date will be removed.
                                            </p>
                                            <NwButton
                                                onClick={() => forceRemoveDate(error.eventDateId)}
                                                label="Force remove"
                                                icon={byPrefixAndName.fass['trash-xmark']}
                                                danger
                                                primary
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </ErrorsWrapper>
            );
        }

        return (
            <ErrorsWrapper>
                <div className="removal-back" onClick={() => setRemovalErrors([])}>
                    <NwIcon icon={byPrefixAndName.fasl['arrow-left']} />
                    <span>Back to models & dates management</span>
                </div>
                <h2>Some models could not be removed</h2>
                <div className="removal-errors">
                    {removalErrors.map((error) => (
                        <div key={error.eventDateModelId} className="removal-error">
                            <h4 className="removal-error-title">
                                {getContactName(error.modelID)} - {error.eventDate}
                            </h4>
                            {error.errorCode === 'DateWithLockedTransactions' && (
                                <div className="removal-error-content">
                                    <div className="removal-error-content-icon">
                                        <NwIcon danger icon={byPrefixAndName.fasl['file-invoice']} />
                                    </div>
                                    <div className="removal-error-content-text">
                                        <p className="locked-transactions-text">
                                            This date has locked transactions (invoiced or paid) and cannot be removed.
                                        </p>
                                    </div>
                                </div>
                            )}
                            {error.errorCode === 'DateWithTransactions' && (
                                <div className="removal-error-content">
                                    <div className="removal-error-content-icon">
                                        <NwIcon danger icon={byPrefixAndName.fasl['message-dollar']} />
                                    </div>
                                    <div className="removal-error-content-text">
                                        <p>
                                            This date has linked transactions. To remove the model from this date, click
                                            on the button below. All transactions for this model on this date will be
                                            removed.
                                        </p>
                                        <NwButton
                                            onClick={() => forceRemoveModelDate(error.eventDateModelId)}
                                            label="Force remove"
                                            icon={byPrefixAndName.fass['trash-xmark']}
                                            danger
                                            primary
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </ErrorsWrapper>
        );
    }

    return (
        <>
            <Header>
                {loading ? (
                    <MiniLoading />
                ) : (
                    <>
                        <div>
                            <Segmented
                                options={['models', 'dates']}
                                value={workOn}
                                onChange={changeWorkOn}
                                disabled={!hasModels}
                            />
                        </div>
                        {workOn === 'models' ? (
                            <p className="n-selected">{selectedModelIds.length} model dates selected</p>
                        ) : (
                            <p className="n-selected">{selectedDateIds.length} date(s) selected</p>
                        )}
                        <div className="select-actions">
                            <SelectLink onClick={handleSelectAll}>select all</SelectLink>
                            <NwDividerVertical />
                            <SelectLink onClick={handleDeselectAll}>deselect all</SelectLink>
                        </div>
                    </>
                )}
            </Header>
            {workOn === 'dates' ? (
                <>
                    {eventDatesWithModelNames.length > 0 && (
                        <Spin spinning={loading}>
                            <DatesContainer>
                                {eventDatesWithModelNames.map((eventDate) => {
                                    const dateChecked = selectedDateIds.find((dateId) => dateId === eventDate.ID);
                                    return (
                                        <div className="date" key={eventDate.ID}>
                                            <div className="date-selection-checkbox-wrapper">
                                                <Checkbox
                                                    checked={dateChecked ? true : false}
                                                    onChange={(e) => handleSelectDate(eventDate.ID, e)}
                                                />
                                            </div>
                                            <VerticalDate minWidth="5rem" date={eventDate.FromDate} showDay />
                                            <div className="models-list">
                                                {eventDate.EventDatesModels.map((model) => {
                                                    return (
                                                        <div
                                                            className={`model${model.Cancel ? ' model-cancelled' : ''}`}
                                                            key={model.ID}
                                                        >
                                                            <div className="model-left">
                                                                <p>
                                                                    {model.Acknowledgment === 'Checked' && (
                                                                        <NwIcon
                                                                            success
                                                                            icon={byPrefixAndName.fass['check']}
                                                                        />
                                                                    )}
                                                                    {model.ModelName.toLowerCase()}
                                                                </p>
                                                                <OptionNumber
                                                                    readOnly
                                                                    key={model.OptionNumber}
                                                                    number={model.OptionNumber}
                                                                    event={eventData}
                                                                    eventDateInfo={eventDate}
                                                                    selectedModel={model}
                                                                    eventDatesModel={model}
                                                                />
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </DatesContainer>
                        </Spin>
                    )}
                    <Footer className={selectedDateIds.length > 0 ? 'is-visible' : ''}>
                        <div className="footer-wrapper">
                            <div className="footer-middle full-width">
                                <label className="footer-label">Remove date(s)</label>
                                <NwRemoveButton onClick={handleRemoveDates} disabled={loading} />
                            </div>
                        </div>
                    </Footer>
                </>
            ) : (
                <>
                    {eventDatesWithModelNames.length > 0 && (
                        <Spin spinning={loading}>
                            <DatesContainer>
                                {eventDatesWithModelNames.map((eventDate) => {
                                    return (
                                        <div className="date" key={eventDate.ID}>
                                            <VerticalDate minWidth="5rem" date={eventDate.FromDate} showDay />
                                            <div className="models-list">
                                                {eventDate.EventDatesModels.map((model) => {
                                                    const modelChecked = selectedModelIds.find(
                                                        (modelId) => modelId === model.ID
                                                    );
                                                    const modelIds = eventDatesModels
                                                        .filter((dateModel) => dateModel.ModelID === model.ModelID)
                                                        .map((model) => model.ID);
                                                    const isModelAddedToAllDates = modelIds.every((val) =>
                                                        selectedModelIds.includes(val)
                                                    );
                                                    const hasModelMoreThanOneDate =
                                                        eventDatesModels.filter((edm) => edm.ModelID === model.ModelID)
                                                            .length > 1;
                                                    return (
                                                        <div
                                                            className={`model${model.Cancel ? ' model-cancelled' : ''}`}
                                                            key={model.ID}
                                                        >
                                                            <div className="model-left">
                                                                <Checkbox
                                                                    checked={modelChecked ? true : false}
                                                                    onChange={(e) => handleSelectModel(model.ID, e)}
                                                                />
                                                                <p>
                                                                    {model.Acknowledgment === 'Checked' && (
                                                                        <NwIcon
                                                                            success
                                                                            icon={byPrefixAndName.fass['check']}
                                                                        />
                                                                    )}
                                                                    {model.ModelName.toLowerCase()}
                                                                </p>
                                                                <OptionNumber
                                                                    readOnly
                                                                    key={model.OptionNumber}
                                                                    number={model.OptionNumber}
                                                                    event={eventData}
                                                                    eventDateInfo={eventDate}
                                                                    selectedModel={model}
                                                                    eventDatesModel={model}
                                                                />
                                                            </div>
                                                            <div className="model-right">
                                                                {modelChecked && hasModelMoreThanOneDate && (
                                                                    <SelectLink
                                                                        onClick={
                                                                            isModelAddedToAllDates
                                                                                ? handleDeselectAllModels(model.ModelID)
                                                                                : handleSelectAllModels(model.ModelID)
                                                                        }
                                                                    >
                                                                        {isModelAddedToAllDates ? 'deselect' : 'select'}{' '}
                                                                        on all dates
                                                                    </SelectLink>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                <FlexContainer mg="16px 0 0">
                                                    <SelectLink
                                                        onClick={handleSelectDateModels(eventDate.EventDatesModels)}
                                                    >
                                                        select all
                                                    </SelectLink>
                                                    <NwDividerVertical />
                                                    <SelectLink
                                                        onClick={handleDeselectDateModels(eventDate.EventDatesModels)}
                                                    >
                                                        deselect all
                                                    </SelectLink>
                                                </FlexContainer>
                                            </div>
                                        </div>
                                    );
                                })}
                            </DatesContainer>
                        </Spin>
                    )}
                    <Footer className={selectedModelIds.length > 0 ? 'is-visible' : ''}>
                        <div className="footer-wrapper">
                            <div className="footer-left">
                                <label className="footer-label">Set as acknowledged / cancelled</label>
                                <div className="footer-left-buttons">
                                    <NwButtonGroup size="small">
                                        <NwButton
                                            disabled={loading}
                                            style={{ color: 'green' }}
                                            icon={byPrefixAndName.fass['check']}
                                            onClick={handleUpdateAcknowledgement(true)}
                                        />
                                        <Button disabled={loading} onClick={handleUpdateAcknowledgement(false)}>
                                            <span className="fa-layers fa-fw fa-lg">
                                                <NwIcon icon={byPrefixAndName.fass['check']} />
                                                <NwIcon icon={byPrefixAndName.fass['slash']} />
                                            </span>
                                        </Button>
                                    </NwButtonGroup>
                                    <ButtonGroupWrapper>
                                        <NwButtonGroup>
                                            <Popover
                                                overlayClassName="nw-popover"
                                                trigger={'click'}
                                                getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                                onClick={(e) => e.stopPropagation()}
                                                onOpenChange={setVisible}
                                                open={visible}
                                                placement="top"
                                                content={
                                                    <CancellationWrapper onClick={(e) => e.stopPropagation()}>
                                                        <div>Cancelled by</div>
                                                        <RadioButtonGroup
                                                            value={customerType}
                                                            onChange={setCustomerType}
                                                            size="small"
                                                            options={[
                                                                { label: 'Client', value: 'Client' },
                                                                { label: 'Agency', value: 'Agency' },
                                                                { label: 'Model', value: 'Model' },
                                                            ]}
                                                        />
                                                        <FlexContainer column mg="8px 0" alignItems="flex-start">
                                                            <div>Notes</div>
                                                            <Input
                                                                autoComplete="off"
                                                                value={notes}
                                                                onChange={(e) => setNotes(e.target.value)}
                                                            />
                                                        </FlexContainer>
                                                        <FlexContainer justifyContent="flex-start" mg="8px 0 0">
                                                            <NwButton
                                                                size="small"
                                                                onClick={handleCancel(true)}
                                                                label="Save"
                                                            />
                                                        </FlexContainer>
                                                    </CancellationWrapper>
                                                }
                                            >
                                                <NwButton
                                                    disabled={loading}
                                                    style={{ color: 'red' }}
                                                    icon={byPrefixAndName.fass['times']}
                                                />
                                            </Popover>
                                            <NwButton
                                                disabled={loading}
                                                onClick={handleCancel(false)}
                                                style={{ color: StyleVariables.LighterBorderColor }}
                                                icon={byPrefixAndName.fass['times']}
                                            />
                                        </NwButtonGroup>
                                    </ButtonGroupWrapper>
                                </div>
                            </div>
                            <div className="footer-middle">
                                <label className="footer-label">Change option number or confirm</label>
                                {isJobEvent(eventData.TypeName) && (
                                    <RadioButtonGroup
                                        disabled={loading}
                                        value={optionNumber}
                                        onChange={handleUpdateOptionNumber}
                                        size="small"
                                        options={[
                                            { value: 10, label: 'job' },
                                            ...[...Array(5).keys()].map((key) => ({ value: key + 1, label: key + 1 })),
                                        ]}
                                    />
                                )}
                            </div>
                            <div className="footer-right">
                                <label className="footer-label">Remove model(s) from date(s)</label>
                                <NwRemoveButton onClick={handleRemoveModelsFromDates} disabled={loading} />
                            </div>
                        </div>
                    </Footer>
                </>
            )}
        </>
    );
};

export default ManageModelsAndDates;
